import React from "react";

import { AlertProps } from "antd";
import cn from "classnames";
import { useNavigate } from "react-router-dom";

import { Button } from "components/Button";
import { StatusBadge } from "components/StatusBadge";
import { EDD_PATH } from "constants/pages";
import { PENDING_REVIEW_TIME, PROCESS_STATUS } from "constants/process";
import { ProcessStatus } from "types/process";

import { Alert } from "./Alert";

import styles from "./Alert.module.scss";

type Props = {
  status: ProcessStatus;
};

type AlertRecord = Record<string, string> & {
  type: AlertProps["type"];
  message: string;
  description: string;
  btnText: string;
};
type DictionaryProp = Record<string, AlertRecord>;

const alertParams: DictionaryProp = {
  [PROCESS_STATUS.draft]: {
    type: "warning",
    message: "Please Review Your Financial Information",
    description:
      "As part of our standard compliance process, please take a moment to confirm your current financial details. You will be asked to provide supporting documents in the next step.",
    btnText: "Continue",
  },
  [PROCESS_STATUS.pending]: {
    type: "warning",
    message: "Please Review Your Financial Information",
    description:
      "Thank you for confirming your financial information and providing your supporting documents. Our team is reviewing them, and we will email you as soon as the review is complete.",
    btnText: "",
  },
  [PROCESS_STATUS.expired]: {
    type: "error",
    message: "Enhanced Due Diligence",
    description: "Unfortunately your EDD expired. Please see steps below and start your verification again.",
    btnText: "Resubmit",
  },
};

export const EddAlert = ({ status }: Props) => {
  const navigate = useNavigate();

  const displayStatusBadge = status === PROCESS_STATUS.pending || status === PROCESS_STATUS.rejected;

  const statusContent = alertParams[status as string];

  if (statusContent !== null) {
    return (
      <Alert
        className={cn(styles.alert, styles.edd)}
        type={statusContent.type}
        message={statusContent.message}
        description={<p className={styles.eddAlertDescriptionText}>{statusContent.description}</p>}
        action={
          <div className={styles.eddAction}>
            <Button
              className={styles.actionBtn}
              loading={status === PROCESS_STATUS.pending}
              onClick={() => navigate(EDD_PATH)}
            >
              {statusContent.btnText}
            </Button>

            {displayStatusBadge && (
              <div className={styles.statusContainer}>
                <span>Status:</span>
                <StatusBadge status={status} iconPosition='right' isTierBox pendingUpTo={PENDING_REVIEW_TIME} />
              </div>
            )}
          </div>
        }
        closable={false}
      />
    );
  } else {
    return (
      <Alert
        type='error'
        message='Something went wrong'
        description='Unexpected status of EDD, please contact support'
        closable={false}
      />
    );
  }
};
